import React, { useMemo } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  normalizeBettorPulse,
  recencySplit,
  stakeHistogram,
  whaleConcentration,
} from "../../utils/bettorsDeskHelpers";
import {
  BettorRecencyBar,
  BettorStakeHistogram,
  WhaleConcentrationBars,
} from "./BettorChartViz";

const BookHero = ({ pulse, concentration, filtered, scopeNote, compact = false }) => {
  const total = pulse.totalBettors;
  const repeatShare = total > 0 ? Math.round((pulse.repeatBettors / total) * 100) : 0;
  const avgStake = total > 0 ? pulse.totalStaked / total : 0;

  const subParts = [];
  if (total > 0) {
    subParts.push(`${total} bettor${total === 1 ? "" : "s"}`);
    subParts.push(`${repeatShare}% repeat`);
    if (avgStake > 0) subParts.push(`${formatCash(avgStake, { compact: true })} avg`);
  }
  if (concentration.leader?.username) {
    subParts.push(`top: ${concentration.leader.username}`);
  }

  return (
    <section className={`bd-book-hero${compact ? " bd-book-hero-compact" : ""}`}>
      <div className="bd-book-hero-main">
        <span className="bd-book-kicker">Your book</span>
        <strong className="bd-book-hero-value tabular-nums">
          {formatCash(pulse.totalStaked, { compact: true })}
        </strong>
        {!compact ? (
          <span className="bd-book-hero-sub">
            {total > 0 ? subParts.join(" · ") : `No bettors in ${scopeNote}`}
          </span>
        ) : null}
      </div>
      {concentration.loaded ? (
        <span
          className={`bd-book-flag ${
            concentration.risky ? "bd-book-flag-risk" : "bd-book-flag-ok"
          }`}
        >
          Top 5 · {concentration.top5Share}%
        </span>
      ) : null}
      {!compact && concentration.risky ? (
        <p className="bd-book-risk-line">
          <AlertTriangle size={10} aria-hidden />
          Handle concentrated in a few bettors
        </p>
      ) : null}
      {!compact && filtered ? (
        <span className="bd-book-scope-note">Filtered — clear filters for house totals</span>
      ) : null}
    </section>
  );
};

const LoyaltyStrip = ({ pulse }) => {
  const total = pulse.totalBettors;
  if (total <= 0) return null;

  const repeatShare = Math.round((pulse.repeatBettors / total) * 100);
  const oneShare = 100 - repeatShare;
  const avgTickets = pulse.totalTickets / total;
  const avgStake = pulse.totalStaked / total;

  return (
    <div className="bd-loyalty-strip">
      <div className="bd-recency-bar" aria-hidden>
        {pulse.repeatBettors > 0 ? (
          <span
            className="bd-recency-seg bd-recency-seg-up"
            style={{ width: `${repeatShare}%` }}
          />
        ) : null}
        {pulse.oneTimeBettors > 0 ? (
          <span
            className="bd-recency-seg bd-recency-seg-accent"
            style={{ width: `${oneShare}%` }}
          />
        ) : null}
      </div>
      <div className="bd-book-inline-stats">
        <span>
          Repeat <strong className="tabular-nums">{repeatShare}%</strong>
        </span>
        <span>
          Avg tix <strong className="tabular-nums">{avgTickets.toFixed(1)}</strong>
        </span>
        <span>
          Avg stake <strong className="tabular-nums">{formatCash(avgStake, { compact: true })}</strong>
        </span>
      </div>
    </div>
  );
};

/**
 * Book intel for the bettors desk.
 * layout="workspace" — horizontal strip above bettor detail (default).
 */
const BettorBookBoard = ({
  pulseRaw,
  loading = false,
  filtered = false,
  topBettors = [],
  houseHandle = null,
  rows = [],
  hasMorePages = false,
  layout = "workspace",
}) => {
  const pulse = useMemo(() => normalizeBettorPulse(pulseRaw), [pulseRaw]);

  const concentration = useMemo(() => {
    const handle =
      houseHandle != null && Number(houseHandle) > 0
        ? Number(houseHandle)
        : !filtered && pulse.loaded
          ? pulse.totalStaked
          : 0;
    return whaleConcentration(topBettors, handle, pulse.loaded ? pulse.totalBettors : 0);
  }, [topBettors, houseHandle, filtered, pulse]);

  const histogram = useMemo(() => stakeHistogram(rows), [rows]);
  const recency = useMemo(() => recencySplit(rows), [rows]);

  const isWorkspace = layout === "workspace";
  const loadedNote = hasMorePages ? "loaded pages" : "loaded rows";
  const scopeNote = filtered ? "this filter" : "this house";

  if (loading) {
    return (
      <section className={`bd-book bd-book-${layout}`} aria-label="Your book">
        <div className="bd-book-loading">
          <Loader2 size={14} className="animate-spin" />
          <span>Reading book…</span>
        </div>
      </section>
    );
  }

  if (!pulse.loaded) return null;

  if (pulse.totalBettors === 0) {
    return (
      <section className={`bd-book bd-book-${layout}`} aria-label="Your book">
        <BookHero
          pulse={pulse}
          concentration={concentration}
          filtered={filtered}
          scopeNote={scopeNote}
          compact={isWorkspace}
        />
        <p className="bd-book-empty">No bettors in this selection yet.</p>
      </section>
    );
  }

  return (
    <section className={`bd-book bd-book-${layout}`} aria-label="Your book">
      <BookHero
        pulse={pulse}
        concentration={concentration}
        filtered={filtered}
        scopeNote={scopeNote}
        compact={isWorkspace}
      />

      <div className="bd-book-grid">
        <article className="bd-book-card">
          <span className="bd-book-kicker">Whales</span>
          {concentration.loaded ? (
            <WhaleConcentrationBars concentration={concentration} compact />
          ) : (
            <p className="bd-book-empty">—</p>
          )}
        </article>

        <article className="bd-book-card">
          <span className="bd-book-kicker">Loyalty</span>
          <LoyaltyStrip pulse={pulse} />
        </article>

        <article className="bd-book-card">
          <span className="bd-book-kicker">Stakes</span>
          <em className="bd-book-card-note">{loadedNote}</em>
          {histogram.loaded ? (
            <BettorStakeHistogram histogram={histogram} ariaLabel="Stake buckets" compact />
          ) : (
            <p className="bd-book-empty">—</p>
          )}
        </article>

        <article className="bd-book-card">
          <span className="bd-book-kicker">Recency</span>
          <em className="bd-book-card-note">{loadedNote}</em>
          {recency.loaded ? (
            <BettorRecencyBar split={recency} ariaLabel="Active vs dormant" compact />
          ) : (
            <p className="bd-book-empty">—</p>
          )}
        </article>
      </div>
    </section>
  );
};

export default BettorBookBoard;
